import http, { AUTH_ROUTES } from "../../../services/api";

/**
 * Poll GET /users/order/:billId/status until a terminal state is reached.
 *
 * Resolves with the token string when status = CONFIRMED.
 * Rejects with an Error when status = FAILED, REFUNDED, or maxAttempts exceeded.
 *
 * Returns a { cancel } handle so callers can stop polling on unmount.
 *
 * @param {string} billId
 * @param {object} [opts]
 * @param {number} [opts.initialIntervalMs=3000]  First poll delay (ms)
 * @param {number} [opts.maxIntervalMs=15000]     Cap for exponential backoff (ms)
 * @param {number} [opts.maxDurationMs=120000]    Hard timeout (ms, default 2 min)
 * @param {Function} [opts.onStatus]              Optional callback: called with each { status, processingState }
 * @returns {{ promise: Promise<string>, cancel: Function }}
 */
export const pollBillStatus = (billId, opts = {}) => {
  const {
    initialIntervalMs = 3000,
    maxIntervalMs = 15000,
    maxDurationMs = 120000,
    onStatus,
  } = opts;

  if (!billId) {
    return {
      promise: Promise.reject(new Error("pollBillStatus: billId is required")),
      cancel: () => {},
    };
  }

  let cancelled = false;
  let activeTimer = null;

  const cancel = () => {
    cancelled = true;
    if (activeTimer) clearTimeout(activeTimer);
  };

  const promise = new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let currentInterval = initialIntervalMs;
    let inFlight = false;

    const check = async () => {
      if (cancelled) return;
      if (inFlight) return; // prevent overlapping requests
      inFlight = true;

      try {
        const res = await http.get(AUTH_ROUTES.GET_BILL_STATUS(billId));
        const data = res?.data?.data;

        if (onStatus && data) onStatus(data);

        if (data?.status === "CONFIRMED" && data?.token) {
          resolve(data.token);
          return;
        }

        if (["FAILED", "REFUNDED", "CANCELLED", "ABANDONED", "DECLINED"].includes(data?.status)) {
          reject(
            new Error(
              data.status === "REFUNDED"
                ? "Payment was refunded — check your payment history."
                : "Payment processing failed — check your payment history.",
            ),
          );
          return;
        }

        if (data?.processingState === "VEND_SUCCEEDED_RECONCILIATION_REQUIRED") {
          const err = new Error("Payment requires review — do not pay again.");
          err.reconciliationRequired = true;
          reject(err);
          return;
        }

        const elapsed = Date.now() - startedAt;
        if (elapsed + currentInterval >= maxDurationMs) {
          reject(
            new Error(
              "Token not yet available — check your payment history shortly.",
            ),
          );
          return;
        }

        // Exponential backoff with jitter, capped at maxIntervalMs
        currentInterval = Math.min(currentInterval * 1.5 + Math.random() * 500, maxIntervalMs);
        activeTimer = setTimeout(check, currentInterval);
      } catch (err) {
        if (cancelled) return;
        // Network error: retry if within time budget
        const elapsed = Date.now() - startedAt;
        if (elapsed + currentInterval >= maxDurationMs) {
          reject(err);
        } else {
          currentInterval = Math.min(currentInterval * 2, maxIntervalMs);
          activeTimer = setTimeout(check, currentInterval);
        }
      } finally {
        inFlight = false;
      }
    };

    activeTimer = setTimeout(check, initialIntervalMs);
  });

  return { promise, cancel };
};

// Legacy alias for backward compat with existing callers
export const pollBillToken = (billId, opts = {}) => {
  const { interval, maxAttempts, ...rest } = opts;
  const mapped = {
    ...rest,
    initialIntervalMs: interval || 3000,
    maxDurationMs: ((maxAttempts || 15) * (interval || 3000)) + 5000,
  };
  return pollBillStatus(billId, mapped).promise;
};
